import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import type { ServicePowerSlideProps, ServicePowerSlideRef } from './ServicePowerSlide.data';
import styles from './ServicePowerSlide.module.scss';
import {
  scrollInTimeline,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './ServicePowerSlide.transitions';

export default function ServicePowerSlide({
  blok,
  ref,
  ...props
}: ServicePowerSlideProps & { ref?: Ref<ServicePowerSlideRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<ServicePowerSlideRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const hasTransitionedIn = useRef<boolean>(false);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        titleRef,
        descriptionRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const scrollTimeline = useRef(
    useSetupTimeline(scrollInTimeline, { titleRef, descriptionRef, textContentRef }),
  );

  const setProgress = useCallback(
    (progress: number, slideIndex: number) => {
      if (slideIndex === props.slideIndex) {
        scrollTimeline.current.progress(progress);

        if (progress > 0.5 && progress < 1 && !hasTransitionedIn.current) {
          hasTransitionedIn.current = true;
          transitionController.transitionIn();
        } else if ((progress <= 0.5 || progress >= 1) && hasTransitionedIn.current) {
          hasTransitionedIn.current = false;
          transitionController.transitionOut();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.slideIndex],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      elementRef,
    }),
    [setProgress, elementRef],
  );

  return (
    <div
      ref={elementRef}
      style={{
        // @ts-ignore
        '--foreground-color': props.config.foregroundColor,
      }}
      className={classNames(styles.servicePowerSlide, props.className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      <div ref={textContentRef} className={classNames(styles.textContent)}>
        {blok.title && (
          <Heading
            ref={titleRef}
            transitionInType="splitWordsSlideInUp"
            transitionOutType="fadeOut"
            as="h4"
            size="heading-05"
            className={classNames(styles.title)}
          >
            {blok.title}
          </Heading>
        )}

        {blok.description && (
          <Text
            ref={descriptionRef}
            // transitionOutType={'fadeOut'}
            transitionInType="splitLineRotateIn"
            transitionOutType="fadeOut"
            as="p"
            size="body-standard"
            className={classNames(styles.description)}
          >
            {blok.description}
          </Text>
        )}
      </div>
    </div>
  );
}
